import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, RefreshCw, XCircle } from "lucide-react";
import { api } from "../lib/api";

// Poll up to ~40s: fast for the first few tries (Apple Pay usually confirms
// within ~2-4s), then a bit slower. Total attempts: 15.
const POLL_SCHEDULE_MS = [
  1200, 1500, 2000, 2000, 2500, 3000, 3000, 3000, 3000, 3500, 3500, 3500, 4000, 4000, 4000,
];

export default function CheckoutStatus() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking | success | expired | timeout | error
  const [detail, setDetail] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const cancelledRef = useRef(false);

  const runPolling = () => {
    cancelledRef.current = false;
    setStatus("checking");
    setAttempt(0);

    const check = async (i) => {
      if (cancelledRef.current) return;
      setAttempt(i + 1);
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (i + 1 >= POLL_SCHEDULE_MS.length) {
          setStatus("timeout");
          return;
        }
        setTimeout(() => check(i + 1), POLL_SCHEDULE_MS[i + 1]);
      } catch {
        if (i + 1 >= POLL_SCHEDULE_MS.length) {
          setStatus("error");
        } else {
          setTimeout(() => check(i + 1), POLL_SCHEDULE_MS[i + 1]);
        }
      }
    };

    setTimeout(() => check(0), POLL_SCHEDULE_MS[0]);
  };

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    runPolling();
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="px-6 pt-10 pb-8" data-testid="checkout-status-page">
      <div className="mm-card text-center">
        {status === "checking" && (
          <>
            <Loader2 className="animate-spin mx-auto text-blue-700 mb-4" size={32} />
            <h1 className="text-xl font-extrabold text-slate-900 mb-1">
              Confirming your payment…
            </h1>
            <p className="text-sm text-slate-500">
              Stripe is finalizing the transaction. This usually takes just a
              few seconds.
            </p>
            <p className="text-[11px] text-slate-400 font-mono mt-3">
              check {attempt}/{POLL_SCHEDULE_MS.length}
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-600 mb-4" size={40} />
            <h1 className="text-xl font-extrabold text-slate-900 mb-1">
              You&apos;re all set!
            </h1>
            <p className="text-sm text-slate-500 mb-4">
              {detail?.scans
                ? `${detail.scans} scans added to your account.`
                : "Your scans have been added."}
            </p>
            <button
              className="mm-btn-primary !h-12 !text-sm"
              onClick={() => navigate("/scan")}
              data-testid="checkout-success-continue"
            >
              Analyze another item
            </button>
          </>
        )}
        {status === "expired" && (
          <>
            <XCircle className="mx-auto text-red-500 mb-4" size={40} />
            <h1 className="text-xl font-extrabold text-slate-900 mb-1">
              Session expired
            </h1>
            <p className="text-sm text-slate-500 mb-4">Please try again.</p>
            <button
              className="mm-btn-secondary"
              onClick={() => navigate("/pricing")}
              data-testid="checkout-back-pricing-btn"
            >
              Back to pricing
            </button>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <Loader2 className="mx-auto text-slate-400 mb-4" size={40} />
            <h1 className="text-xl font-extrabold text-slate-900 mb-1">
              Still processing
            </h1>
            <p className="text-sm text-slate-500 mb-4">
              Stripe is taking a little longer than usual. If you completed
              payment, your scans will appear as soon as we hear back — no
              double charge, ever.
            </p>
            <div className="flex flex-col gap-2">
              <button
                className="mm-btn-primary !h-12 !text-sm"
                onClick={runPolling}
                data-testid="checkout-check-again-btn"
              >
                <RefreshCw size={14} />
                Check again
              </button>
              <button
                className="mm-btn-secondary !h-11"
                onClick={() => navigate("/dashboard")}
                data-testid="checkout-go-dashboard-btn"
              >
                Go to dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
